import type { Locale } from "@/translations";

/** Default locale: Arabic — no URL prefix. */
export const DEFAULT_LOCALE: Locale = "ar";

/** English uses `/en` prefix on all routes. */
export const PREFIXED_LOCALE: Locale = "en";

export const LOCALE_HEADER_NAME = "x-petromin-locale";

/**
 * True if pathname is the English tree (`/en` or `/en/...`).
 */
export function pathnameIsEnglish(pathname: string): boolean {
  return pathname === "/en" || pathname.startsWith("/en/");
}

/**
 * Locale implied by the URL (not cookie).
 */
export function localeFromPathname(pathname: string): Locale {
  return pathnameIsEnglish(pathname) ? "en" : "ar";
}

/**
 * Path without `/en` prefix (leading slash, `/` for home). Used to switch locale while keeping the same page.
 */
export function stripEnglishPrefix(pathname: string): string {
  if (pathname === "/en") return "/";
  if (pathname.startsWith("/en/")) {
    const rest = pathname.slice(3);
    return rest.startsWith("/") ? rest : `/${rest}`;
  }
  return pathname;
}

/**
 * Build URL for `locale` from a **canonical** internal path (always without `/en`, e.g. `/about`, `/services/foo`).
 */
export function withLocalePrefix(locale: Locale, canonicalPath: string): string {
  const path = canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`;
  if (locale === "ar") {
    if (pathnameIsEnglish(path)) return stripEnglishPrefix(path);
    return path === "" ? "/" : path;
  }
  if (pathnameIsEnglish(path)) return path;
  if (path === "/") return "/en";
  return `/en${path}`;
}

/**
 * Convert current browser pathname to canonical path (strip `/en`).
 */
export function pathnameToCanonical(pathname: string): string {
  return stripEnglishPrefix(pathname);
}
